import { put, takeLatest } from 'redux-saga/effects';
import {
  GoogleAnalyticsActions,
  CodePanelActions,
  savedCode,
  embeddingTypeString,
  OperationsMenuActions,
  ExampleGalleryActions,
  exampleIdString,
  EnvironmentSettingActions,
} from '../../constants';

import { templateInitialCode, exampleTemplates } from '../../utils/templateGenerator';
import { USE_DEMO_EXAMPLES_FROM_LINK } from '../../constants/google-analytics';
import EmbeddingType from '../../utils/embeddingType';

// Loads in code and dark mode settings from local storage
function* loadCodeFromLocalStorage() {
  if (window.localStorage) {
    // Fetch the saved code from local storage
    const content = localStorage.getItem(savedCode);
    if (content) {
      yield put({ type: CodePanelActions.UPDATE_CONTENT, content, updateLocalContent: true });
    }
    // Fetch the embedding type from local storage
    let embeddingType = localStorage.getItem(embeddingTypeString);
    if (embeddingType === 'Bot') {
      embeddingType = 'Agent';
    }
    if (embeddingType) {
      yield put({ type: EnvironmentSettingActions.CHANGE_TYPE, embeddingType });
    } else {
      // If the embedding type is not set, set it to default
      localStorage.setItem(embeddingTypeString, EmbeddingType.Dashboard);
      yield put({
        type: EnvironmentSettingActions.CHANGE_TYPE,
        embeddingType: EmbeddingType.Dashboard,
      });
    }
    // Check if the user is new and display the example gallery
    if (!window.localStorage.getItem('isNewUser')) {
      window.localStorage.setItem('isNewUser', 'true');
      yield put({ type: ExampleGalleryActions.SET_IS_OPEN, isOpen: true });
    }
    // If the local storage doesn't exist or is null use the default dashboard iframe embedding type
    if (window.localStorage.getItem(exampleIdString)) {
      yield put({ type: ExampleGalleryActions.SET_IS_OPEN, isOpen: true });
      yield put({ type: ExampleGalleryActions.ON_LOAD_LINKED_EXAMPLE });
    }
  }
}

// Load the linked example from local storage to the selected example. If it isn't valid use default example.
/* eslint-disable no-restricted-syntax */
function* loadExampleFromLocalStorage() {
  if (window.localStorage) {
    let exampleId = window.localStorage.getItem(exampleIdString);
    window.localStorage.removeItem(exampleIdString);
    if (exampleId) {
      try {
        exampleId = decodeURIComponent(exampleId);
      } catch (e) {
        return;
      }
      let embeddingType = localStorage.getItem(embeddingTypeString);
      if (embeddingType === 'Bot') {
        embeddingType = 'Agent';
      }
      for (const embedTypeContent of Object.values(exampleTemplates)) {
        for (const exampleGroup of Object.values(embedTypeContent.content)) {
          for (const example of Object.values(exampleGroup.items)) {
            if (example.id === exampleId) {
              // if the embedding type of exampleId is not the same as the existing embedding type, switch the embedding type
              if (embedTypeContent.embeddingType !== embeddingType) {
                yield put({
                  type: OperationsMenuActions.SAVE_CHANGE_TYPE,
                  embeddingType: embedTypeContent.embeddingType,
                  useExampleId: true,
                });
              }
              yield put({
                type: ExampleGalleryActions.SET_EXAMPLE_ID,
                exampleId,
              });
              // Dispatch an action to GA saga to record this event
              yield put({
                type: GoogleAnalyticsActions.SEND_GA_EVENT,
                eventLabel: example.name,
                eventName: USE_DEMO_EXAMPLES_FROM_LINK,
              });
              return;
            }
          }
        }
      }
    }
    console.error(
      'The example could not be found in current embedding mode. Please make sure the url and example id are valid.',
    );
  }
}
/* eslint-enable no-restricted-syntax */

function* loadTemplateCode(action) {
  const content = yield templateInitialCode(
    action.embeddingType,
    action.exampleName,
    action.exampleGroupName,
  );
  yield put({ type: CodePanelActions.UPDATE_CONTENT, content, updateLocalContent: true });
}

function* setCodeToLocalStorage(action) {
  // Save the code in action content to local storage
  if (window.localStorage) {
    yield localStorage.setItem(savedCode, action.content);
  }
}

// This saga handles changes to the code panel
export default function* codePanelSaga() {
  yield takeLatest(ExampleGalleryActions.ON_LOAD_LINKED_EXAMPLE, loadExampleFromLocalStorage);
  yield takeLatest(CodePanelActions.ON_LOAD, loadCodeFromLocalStorage);
  yield takeLatest(CodePanelActions.SAVE_CODE, setCodeToLocalStorage);
  yield takeLatest(CodePanelActions.ON_SELECTION, loadTemplateCode);
}
